import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Box, Typography } from '@mui/material';

import { FCheckbox, FHSelect } from '../../../../base';
import { useTreatmentPlanEditable } from '../../hooks/useTreatmentPlanEditable';
import { capitalizeFirstLetter } from '../../../../../helpers';
import { Themes, BackgroundTypes } from '../../../../../constants';
import { GOAL_DETAILS_FIELD_IDS } from '../common/constants/general';
import { GoalTypes } from './constants';
import HypothesizedFunction from '../common/components/hypothesizedFunction';

import type {
  IBehavior,
  ITreatmentPlanGoal,
  ITreatmentPlanSymptom,
  SelectValueType,
} from '../../../../../utils/types';
import type { ChangeEvent } from 'react';

interface LongTermGoalSectionProps {
  goal: ITreatmentPlanGoal;
  treatmentPlanId: number;
  goalType: GoalTypes;
  errors: any;
  onUpdateField: (key: string, value: any) => void;
}

function LongTermGoalSection(props: LongTermGoalSectionProps) {
  const { goal, goalType, errors, treatmentPlanId, onUpdateField } = props;
  const isTpEditable = useTreatmentPlanEditable();
  const ref = useRef<HTMLDivElement | null>(null);
  const { list: symptoms } = useSelector((state: any) => state.symptoms);
  const { list: behaviors } = useSelector((state: any) => state.behaviors);
  const [symptomsRefWidth, setSymptomsRefWidth] = useState<number>();
  const [isEditable, setIsEditable] = useState<boolean>(false);

  useEffect(() => {
    if (!ref || !ref.current) return;

    setSymptomsRefWidth(ref.current.offsetWidth);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref.current]);

  useEffect(() => {
    if (!goal) return;

    setIsEditable(isTpEditable && goal.is_active);
  }, [isTpEditable, goal]);

  return (
    <Box sx={{ display: 'flex', flexGrow: 1, flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', flexGrow: 1, alignItems: 'center' }}>
        <Typography fontWeight={'bold'} mr={2}>
          Focus area:*
        </Typography>
        <FHSelect
          sx={{ flexGrow: 1 }}
          inputHeight={40}
          colorTheme={Themes.Primary}
          backgroundType={BackgroundTypes.Colored}
          disabled={!isEditable}
          options={behaviors
            .filter((behavior: IBehavior): behavior is IBehavior & { id: number } =>
              behavior.id !== undefined,
            )
            .map((behavior: IBehavior & { id: number }) => ({
              value: behavior.id,
            label: capitalizeFirstLetter(behavior.label),
            }))}
          value={goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]?.behavior?.id || ''}
          onUpdate={(value: SelectValueType) =>
            setTimeout(() => {
              onUpdateField(GOAL_DETAILS_FIELD_IDS.GOAL_NAME, {
                ...goal[GOAL_DETAILS_FIELD_IDS.GOAL_NAME],
                behavior_id: value,
              });
            })
          }
          error={errors && errors[GOAL_DETAILS_FIELD_IDS.GOAL_NAME]}
        />
      </Box>
      {goal?.goal_type === GoalTypes.BehaviorReduction && (
        <Box sx={{ mt: 3 }}>
          <HypothesizedFunction
            treatmentPlanId={treatmentPlanId}
            goalId={goal.id}
            hypothesizedFunctions={goal.hypothesized_functions ?? []}
            isEditable={isEditable && Boolean(goal?.is_editing)}
          />
        </Box>
      )}
      {!!(goalType === GoalTypes.BehaviorReduction && symptoms.length > 0) && (
        <Box sx={{ display: 'flex', flexDirection: 'column' }}>
          <Box sx={{ display: 'flex', pt: 3, pb: 1, position: 'relative' }} ref={ref}>
            <Typography fontWeight={'bold'} mr={2}>
              Is a maladaptive behavior related to this goal?*
            </Typography>
            <FCheckbox
              label="Yes"
              colorTheme={Themes.Primary}
              coloredBackground
              disabled={!isEditable}
              checked={goal[GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION]}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onUpdateField(
                  GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION,
                  e.target.checked ? true : false,
                )
              }
            />
            <FCheckbox
              label="No"
              colorTheme={Themes.Primary}
              coloredBackground
              disabled={!isEditable}
              checked={!goal[GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION]}
              onChange={(e: ChangeEvent<HTMLInputElement>) =>
                onUpdateField(
                  GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION,
                  e.target.checked ? false : true,
                )
              }
            />
            <Typography fontWeight={'bold'} ml={2}>
              If yes, please include DSM5 description below:
            </Typography>
          </Box>
          {symptoms.length > 0 && goal[GOAL_DETAILS_FIELD_IDS.IS_MALADAPTIVE_CORRECTION] && (
            <FHSelect
              sx={{ flexGrow: 1, width: symptomsRefWidth ? symptomsRefWidth : 'auto' }}
              inputHeight={40}
              colorTheme={Themes.Primary}
              backgroundType={BackgroundTypes.EvenColored}
              popoverWidth={symptomsRefWidth}
              disabled={!isEditable}
              options={symptoms
                .filter((symptom: ITreatmentPlanSymptom): symptom is ITreatmentPlanSymptom & { id: number } =>
                  symptom.id !== undefined,
                )
                .map((symptom: ITreatmentPlanSymptom & { id: number }) => ({
                  value: symptom.id,
                label: `${symptom.label} - ${symptom.associated_dsm5_symptom}`,
                }))}
              value={goal[GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID] || symptoms[0].id}
              onUpdate={(value: SelectValueType) =>
                setTimeout(() => {
                  onUpdateField(GOAL_DETAILS_FIELD_IDS.MALADAPTIVE_SYMPTOM_ID, value);
                })
              }
            />
          )}
        </Box>
      )}
    </Box>
  );
}

export default LongTermGoalSection;
